import { Fetcher as FetcherSpirit, Route, Token as TokenSpirit } from '@spiritswap/sdk';
import { Fetcher as FetcherSpooky, Route as RouteSpooky, Token as TokenSpooky /*, Route as RouteSpooky*/ } from '@spookyswap/sdk'
import { Configuration } from './config';
import { ContractName, TokenStat, AllocationTime, LPStat, Bank, PoolStats, TShareSwapperStat, AccountInfo, AutoCompounder, AutocompounderStats, BondStat } from './types';
import { BigNumber, Contract, ethers, EventFilter } from 'ethers';
import { decimalToBalance } from './ether-utils';
import { TransactionResponse } from '@ethersproject/providers';
import ERC20 from './ERC20';
import { getFullDisplayBalance, getDisplayBalance } from '../utils/formatBalance';
import { getDefaultProvider } from '../utils/provider';
import IUniswapV2PairABI from './IUniswapV2Pair.abi.json';
import GaugeABI from './Gauge.abi.json';
import config, { bankDefinitions } from '../config';
import moment from 'moment';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import { ETH_TICKER, SPIRIT_ROUTER_ADDR, HOG_TICKER, getBaseApiUrl, OS_TICKER, SWAPX_ROUTER_ADDR } from '../utils/constants';

// Add these interfaces at the top of the file, after the imports
interface PriceCacheEntry {
    price: string;
    timestamp: number;
}

interface TokenPriceCache {
    data: any;
    timestamp: number;
}

// Add this new interface near the top with other interfaces
interface AccountBalances {
  hog: string;
  ghog: string;
  hogOsLp: string;
  ghogOsLp: string;
}

/**
 * An API module of Hog Finance contracts.
 * All contract-interacting domain logic should be defined in here.
 */
export class TombFinance {
    myAccount: string;
    provider: ethers.providers.Web3Provider;
    signer?: ethers.Signer;
    config: Configuration;
    contracts: { [name: string]: Contract };
    externalTokens: { [name: string]: ERC20 };
    masonryVersionOfUser?: string;

    HOGOSLP: Contract;
    GHOGOSLP: Contract;

    HOG_OS_LP: ERC20;
    GHOG_OS_LP: ERC20;

    HOG: ERC20;
    GHOG: ERC20;
    BHOG: ERC20;

    S: ERC20;
    OS: ERC20;
    ANON: ERC20;
    SHADOW: ERC20;
    scUSD: ERC20;
    Beets: ERC20;
    GOGLZ: ERC20;
    MCLB: ERC20;
    stS: ERC20;
    x33: ERC20;
    "USDC.e": ERC20;

    private priceCache: Map<string, PriceCacheEntry> = new Map();
    private CACHE_DURATION = 60 * 1000; // 1 minute in milliseconds

    private fetching = false; // Flag to prevent multiple concurrent requests

    // For the shadow API cache
    private tokenPriceCache: TokenPriceCache = {
        data: null,
        timestamp: 0
    };
    private CACHE_TTL = 60 * 1000; // 1 minute in milliseconds

    constructor(cfg: Configuration) {
        const { deployments, externalTokens } = cfg;
        const provider = getDefaultProvider();

        // loads contracts from deployments
        this.contracts = {};
        for (const [name, deployment] of Object.entries(deployments)) {
            this.contracts[name] = new Contract(deployment.address, deployment.abi, provider);
        }
        this.externalTokens = {};
        for (const [symbol, [address, decimal]] of Object.entries(externalTokens)) {
            this.externalTokens[symbol] = new ERC20(address, provider, symbol, decimal);
        }
        this.HOG = new ERC20(deployments.tomb.address, provider, 'HOG');
        this.GHOG = new ERC20(deployments.tShare.address, provider, 'GHOG');
        this.BHOG = new ERC20(deployments.tBond.address, provider, 'BHOG');
        this.S = this.externalTokens['S'];
        this.OS = this.externalTokens['OS'];
        this.ANON = this.externalTokens['ANON'];
        this.SHADOW = this.externalTokens['SHADOW'];
        this.scUSD = this.externalTokens['scUSD'];
        this.Beets = this.externalTokens['Beets'];
        this.GOGLZ = this.externalTokens['GOGLZ'];
        this.MCLB = this.externalTokens['MCLB'];
        this.stS = this.externalTokens['stS'];
        this.x33 = this.externalTokens['x33'];

        this["USDC.e"] = this.externalTokens['USDC.e'];

        this.HOGOSLP = new Contract("0x784dd93f3c42dcbf88d45e6ad6d3cc20da169a60", IUniswapV2PairABI, provider);
        this.GHOGOSLP = new Contract(externalTokens['GHOG-OS-LP'][0], IUniswapV2PairABI, provider);

        this.config = cfg;
        this.provider = provider;

        // These should be properly initialized in the constructor
        this.HOG_OS_LP = new ERC20(this.config.externalTokens['HOG-OS-LP'][0], this.provider, 'HOG-OS-LP');
        this.GHOG_OS_LP = new ERC20(this.config.externalTokens['GHOG-OS-LP'][0], this.provider, 'GHOG-OS-LP');
    }

    /**
     * @param provider From an unlocked wallet. (e.g. Metamask)
     * @param account An address of unlocked wallet account.
     */
    unlockWallet(provider: any, account: string) {
        const newProvider = provider;
        // const newProvider = new ethers.providers.Web3Provider(provider, this.config.chainId);
        this.signer = newProvider.getSigner(0);
        this.myAccount = account;
        for (const [name, contract] of Object.entries(this.contracts)) {
            this.contracts[name] = contract.connect(this.signer);
        }
        const tokens = [this.HOG, this.GHOG, this.BHOG, ...Object.values(this.externalTokens)];
        for (const token of tokens) {
            token.connect(this.signer);
        }
        this.HOGOSLP = this.HOGOSLP.connect(this.signer);
        console.log(`🔓 Wallet is unlocked. Welcome, ${account}!`);
        this.fetchMasonryVersionOfUser()
            .then((version) => (this.masonryVersionOfUser = version))
            .catch((err) => {
                console.error(`Failed to fetch nest version: ${err.stack}`);
                this.masonryVersionOfUser = 'latest';
            });
    }

    get isUnlocked(): boolean {
        return !!this.myAccount;
    }

    //===================================================================
    //===================== GET ASSET STATS =============================
    //===================FROM SPOOKY TO DISPLAY =========================
    //=========================IN HOME PAGE==============================
    //===================================================================

    async getExternalTokenPrice(tokenName: string): Promise<TokenStat> {
        const priceInUSD = await this.getTokenPriceFromCustomApi(this.externalTokens[tokenName]);
        const priceOfOneFTM = await this.getOSPriceFromDexScreener();
        const priceOfTombInDollars = Number(priceInUSD).toFixed(2);
        const priceInFTM = ((Number(priceInUSD) / Number(priceOfOneFTM))).toFixed(2);

        return {
            tokenInFtm: priceInFTM,
            priceInDollars: priceOfTombInDollars,
            totalSupply: '0',
            circulatingSupply: '0',
        };
    }

    async getTombStat(): Promise<TokenStat> {
        const { HogGenesisRewardPool } = this.contracts;
        const supply = await this.HOG.totalSupply();
        const tombRewardPoolSupply = await this.HOG.balanceOf("0x2e585b96a2ef1661508110e41c005be86b63fc34");
        const tombCirculatingSupply = supply
            .sub(tombRewardPoolSupply)
        // const priceInUSD = await this.getTokenPriceFromCustomApi(this.HOG);
        const priceInOs = await this.getHOGPriceFromDexScreener();
        console.log("priceInOs", priceInOs);
        const priceOfOneOS = await this.getOSPriceFromDexScreener();
        console.log("priceInOs2", priceOfOneOS);
        const priceOfTombInDollars = (Number(priceInOs) * Number(priceOfOneOS)).toFixed(2);
        //const priceInFTM = ((Number(priceInOs) / Number(priceOfOneOS))).toFixed(2);

        return {
            tokenInFtm: priceInOs,
            priceInDollars: priceOfTombInDollars,
            totalSupply: getDisplayBalance(supply, this.HOG.decimal, 0),
            circulatingSupply: getDisplayBalance(tombCirculatingSupply, this.HOG.decimal, 0),
        };
    }

    /**
     * Calculates various stats for the requested LP
     * @param name of the LP token to load stats for
     * @returns
     */
    async getLPStat(name: string): Promise<LPStat> {
        const lpToken = this.externalTokens[name];
        const lpTokenSupplyBN = await lpToken.totalSupply();
        const lpTokenSupply = getDisplayBalance(lpTokenSupplyBN, lpToken.decimal);
        const token0 = name.startsWith('HOG') || name.startsWith('GHOG') ? (name.startsWith('HOG') ? this.HOG : this.GHOG) : this.externalTokens[name.substr(0, name.indexOf('-'))];
        const isTomb = name.startsWith('HOG');
        const tokenAmountBN = await token0.balanceOf(lpToken.address);
        const tokenAmount = getDisplayBalance(tokenAmountBN, token0.decimal);

        let ftmAmountBN = await this["USDC.e"].balanceOf(lpToken.address);
        let ftmAmount = getDisplayBalance(ftmAmountBN, 6);

        if (name.startsWith('-USDC.e', name.substr(0, name.indexOf('-')).length)) {
            ftmAmountBN = await this["USDC.e"].balanceOf(lpToken.address);
            ftmAmount = getDisplayBalance(ftmAmountBN, 6);
        }
        else if (name.startsWith('-OS', name.substr(0, name.indexOf('-')).length)) {
            ftmAmountBN = await this["OS"].balanceOf(lpToken.address);
            ftmAmount = getDisplayBalance(ftmAmountBN, 18);
        }
        else {
            ftmAmountBN = await this.GHOG.balanceOf(lpToken.address);
            ftmAmount = getDisplayBalance(ftmAmountBN, 18);
        }
        const tokenAmountInOneLP = Number(tokenAmount) / Number(lpTokenSupply);
        const ftmAmountInOneLP = Number(ftmAmount) / Number(lpTokenSupply);
        const lpTokenPrice = await this.getLPTokenPrice(lpToken, token0, isTomb);
        const lpTokenPriceFixed = Number(lpTokenPrice).toFixed(2).toString();
        const liquidity = (Number(lpTokenSupply) * Number(lpTokenPrice)).toFixed(2).toString();

        return {
            tokenAmount: tokenAmountInOneLP.toFixed(2).toString(),
            ftmAmount: ftmAmountInOneLP.toFixed(2).toString(),
            priceOfOne: lpTokenPriceFixed,
            totalLiquidity: liquidity,
            totalSupply: Number(lpTokenSupply).toFixed(2).toString(),
        };
    }

    /**
     * Use this method to get price for Dollar
     * @returns TokenStat for BHOG
     * priceInFTM
     * priceInDollars
     * TotalSupply
     * CirculatingSupply (always equal to total supply for bonds)
     */
    async getBondStat(): Promise<BondStat> {
        const { TreasuryV3, Treasury } = this.contracts;
        const hogStat = await this.getTombStat(); // Renamed to reflect HOG instead of Tomb
        
        // Get the discount or premium rate based on current price conditions
        const hogPrice = await TreasuryV3.getHogPrice();
        const hogPriceOne = await TreasuryV3.hogPriceOne();
    
        let modifier = 1;
        let bondPriceInFTM, priceOfBHOGInDollars;

        if(parseFloat(ethers.utils.formatUnits(hogPrice, 18)).toFixed(4) > parseFloat(ethers.utils.formatUnits(hogPriceOne, 18)).toFixed(4)) {
            // If HOG price > $1, use premium rate for redemption
            const bondPremiumRate = await TreasuryV3.getBondPremiumRate();
            modifier = bondPremiumRate / 1e18;
            bondPriceInFTM = (Number(hogStat.tokenInFtm) / modifier).toFixed(4);

            // Calculate BHOG price in dollars
                priceOfBHOGInDollars = (Number(hogStat.priceInDollars) / modifier).toFixed(4);
        
        } else {
            // If HOG price <= $1, use discount rate for purchases
            const bondDiscountRate = await TreasuryV3.getBondDiscountRate();
            modifier = bondDiscountRate / 1e18;
            console.log("modifier",Number(hogStat.tokenInFtm), modifier);
            // Calculate BHOG price in terms of network token (FTM/ETH/etc)
            bondPriceInFTM = (Number(hogStat.tokenInFtm) * modifier).toFixed(4);

        // Calculate BHOG price in dollars
            priceOfBHOGInDollars = (Number(hogStat.priceInDollars) * modifier).toFixed(4);
        
        }


        
        const supply = await this.BHOG.displayedTotalSupply();
        
        return {
            tokenInFtm: bondPriceInFTM,
            priceInDollars: priceOfBHOGInDollars,
            totalSupply: supply,
            hogprice: modifier,
            circulatingSupply: supply,
        };
    }

    /**
     * @returns TokenStat for GHOG
     * priceInFTM
     * priceInDollars
     * TotalSupply
     * CirculatingSupply (always equal to total supply for bonds)
     */
    async getShareStat(): Promise<TokenStat> {
        const { CemeteryHogOsRewardPool } = this.contracts;

        const supply = await this.GHOG.totalSupply();

        const priceInUSD = await this.getTokenPriceFromCustomApi(this.GHOG);
        console.log("priceInUSssD", priceInUSD);
        const tombRewardPoolSupply = await this.GHOG.balanceOf(CemeteryHogOsRewardPool.address);

        const tShareCirculatingSupply = supply.sub(tombRewardPoolSupply);

        const priceOfOneFTM = await this.getOSPriceFromDexScreener();

        const priceOfSharesInDollars = Number(priceInUSD).toFixed(2);
        const priceInETH = ((Number(priceInUSD) / Number(priceOfOneFTM))).toFixed(2);

        return {
            tokenInFtm: priceInETH,
            priceInDollars: priceOfSharesInDollars,
            totalSupply: getDisplayBalance(supply, this.GHOG.decimal, 0),
            circulatingSupply: getDisplayBalance(tShareCirculatingSupply, this.GHOG.decimal, 0),
        };
    }

    async getTombStatInEstimatedTWAP(): Promise<TokenStat> {
        const { SeigniorageOracle, HogGenesisRewardPool } = this.contracts;
        const expectedPrice = await SeigniorageOracle.twap(this.HOG.address, ethers.utils.parseEther('1'));

        const supply = await this.HOG.totalSupply();
        const tombRewardPoolSupply = await this.HOG.balanceOf(HogGenesisRewardPool.address);
        const tombCirculatingSupply = supply.sub(tombRewardPoolSupply);
        return {
            tokenInFtm: getDisplayBalance(expectedPrice),
            priceInDollars: getDisplayBalance(expectedPrice),
            totalSupply: getDisplayBalance(supply, this.HOG.decimal, 0),
            circulatingSupply: getDisplayBalance(tombCirculatingSupply, this.HOG.decimal, 0),
        };
    }

    async getTombPriceInLastTWAP(): Promise<BigNumber> {
        const { TreasuryV3 } = this.contracts;
        return TreasuryV3.getHogUpdatedPrice();
    }

    async getBondsPurchasable(): Promise<BigNumber> {
        const { TreasuryV3 } = this.contracts;
        return TreasuryV3.getBurnableHogLeft();
    }

    async getZeroAddressBalances(): Promise<{ hog: BigNumber; hogOsLp: BigNumber; ghogOsLp: BigNumber }> {
        const zeroAddress = '0x0000000000000000000000000000000000000000';

        try {
            const hogBalance = await this.externalTokens["HOG"].balanceOf(zeroAddress);
            const hogOsLpBalance = await this.externalTokens["HOG-OS-LP"].balanceOf(zeroAddress);
            const ghogOsLpBalance = await this.externalTokens["GHOG-OS-LP"].balanceOf(zeroAddress);

            return {
                hog: hogBalance,
                hogOsLp: hogOsLpBalance,
                ghogOsLp: ghogOsLpBalance
            };
        } catch (error) {
            console.error('Error fetching zero address balances:', error);
            throw error;
        }
    }

    /**
     * Calculates the TVL, APR and daily APR of a provided pool/bank
     * @param bank
     * @returns
     */
    async getPoolAPRs(bank: Bank): Promise<PoolStats> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const depositToken = bank.depositToken;
        const poolContract = this.contracts[bank.contract];

        const depositTokenPrice = await this.getDepositTokenPriceInDollars(bank.depositTokenName, depositToken);

        let stakeInPool;
        if (bank.contract === 'CemeteryHogOsRewardPool') {
            // Create contract instance for 0x4331Cb6903D9c6C0723721A77435a610E1e933Cf
            const hogOsContract = new Contract('0x4331Cb6903D9c6C0723721A77435a610E1e933Cf', GaugeABI, this.provider);
            // Check balance of 0xa7141905e2972c295577882552bede5406daf5ec in this contract
            stakeInPool = await hogOsContract.balanceOf('0xa7141905e2972c295577882552bede5406daf5ec');
        } else if (bank.contract === 'CemeteryGHogOsRewardPool') {
            // Create contract instance for 0x5342E272769e797e4bF34698437Eb28bF49c315d
            const ghogOsContract = new Contract('0x5342E272769e797e4bF34698437Eb28bF49c315d', GaugeABI, this.provider);
            // Check balance of 0xa7141905e2972c295577882552bede5406daf5ec in this contract
            // For GHOG-OS pool, check balance at 0x5342E272769e797e4bF34698437Eb28bF49c315d
            stakeInPool = await ghogOsContract.balanceOf('0xa7141905e2972c295577882552bede5406daf5ec');
        } else {
            // For other pools, use the default behavior
            stakeInPool = await depositToken.balanceOf(bank.address);
        }

        const TVL = Number(depositTokenPrice) * Number(getDisplayBalance(stakeInPool, depositToken.decimal));
        const stat = bank.earnTokenName === 'HOG' ? await this.getTombStat() : await this.getShareStat();
        const tokenPerSecond = bank.earnTokenName === 'HOG' ? await this.getTokenPerSecond(bank.earnTokenName, bank.contract, poolContract, bank.depositTokenName, bank.poolId) : await this.getElysiumTokenPerSecond(bank)

        // console.log("tokenPerSecond", tokenPerSecond.toString(), bank.depositTokenName);

        const tokenPerHour = tokenPerSecond.mul(60).mul(60);
        const totalRewardPricePerYear =
            Number(stat.priceInDollars) * Number(getDisplayBalance(tokenPerHour.mul(24).mul(365)));
        const totalRewardPricePerDay = Number(stat.priceInDollars) * Number(getDisplayBalance(tokenPerHour.mul(24)));
        const totalStakingTokenInPool =
            Number(depositTokenPrice) * Number(getDisplayBalance(stakeInPool, depositToken.decimal));
        const dailyAPR = (totalRewardPricePerDay / totalStakingTokenInPool) * 100;
        const yearlyAPR = (totalRewardPricePerYear / totalStakingTokenInPool) * 100;
        return {
            dailyAPR: dailyAPR.toFixed(2).toString(),
            yearlyAPR: yearlyAPR.toFixed(2).toString(),
            TVL: TVL.toFixed(0).toString(),
        };
    }

    async getGenesisTime(): Promise<number> {
        const { HogGenesisRewardPool } = this.contracts;
        const poolStartTime = await HogGenesisRewardPool.poolStartTime();
        const poolEndTime = await HogGenesisRewardPool.poolEndTime();
        const currentTime = new Date();
        const startDateTime = new Date(poolStartTime.toNumber() * 1000);

        // If current time is before start time, count down to start time
        if (currentTime < startDateTime) {
            return startDateTime.getTime() - currentTime.getTime();
        }

        // If start time has passed, count down to end time
        const endDateTime = new Date(poolEndTime.toNumber() * 1000);
        return endDateTime.getTime() - currentTime.getTime();
    }

    /**
     * Method to return the amount of tokens the pool yields per second
     * @param earnTokenName the name of the token that the pool is earning
     * @param contractName the contract of the pool/bank
     * @param poolContract the actual contract of the pool
     * @returns
     */
    async getTokenPerSecond(
        earnTokenName: string,
        contractName: string,
        poolContract: Contract,
        depositTokenName: string,
        poolId: number,
    ) {
        if (earnTokenName === 'HOG') {
            // if (!contractName.endsWith('TombRewardPool')) {

            //     // const rewardPerSecond = await poolContract.hogPerSecond();
            //     const poolInfo = await poolContract.poolInfo(poolId);
            //     const poolAlloc = poolInfo.poolHogPerSec;
            //     return poolAlloc;
            // }
            const poolStartTime = await poolContract.poolStartTime();
            const startDateTime = new Date(poolStartTime.toNumber() * 1000);
            const SEVENDAYS = 7 * 24 * 60 * 60 * 1000;
            if (Date.now() - startDateTime.getTime() > SEVENDAYS) {
                //   return await poolContract.epochTombPerSecond(1);
                return BigNumber.from(0);
            }
            if (depositTokenName.startsWith('HOG-OS-LP')) {
                return parseUnits("0.318750000", 18);
            } else if (depositTokenName.startsWith('OS')) {
                return parseUnits("0.224305556", 18);
            } else if (depositTokenName.startsWith('ANON')) {
                return parseUnits("0.118055556", 18);
            } else if (depositTokenName.startsWith('MCLB')) {
                return parseUnits("0.106250000", 18);
            } else if (depositTokenName.startsWith('SWPx')) {
                return parseUnits("0.129861111", 18);
            } else if (depositTokenName.startsWith('stS')) {
                return parseUnits("0.082638889", 18);
            } else if (depositTokenName.startsWith('scUSD')) {
                return parseUnits("0.082638889", 18);
            } else if (depositTokenName.startsWith('INDI')) {
                return parseUnits("0.047222222", 18);
            }
            else if (depositTokenName.startsWith('Beets')) {
                return parseUnits("0.023611111", 18);
            }
            else if (depositTokenName.startsWith('GOGLZ')) {
                return parseUnits("0.047222222", 18);
            }
            else {
                return BigNumber.from(0);
            }
        }

        // const rewardPerSecond = await poolContract.sharePerSecond();
        // const poolInfo = await poolContract.poolInfo(poolId);
        // const poolAlloc = poolInfo.poolGhogPerSec;
        // return poolAlloc;
        if (depositTokenName.startsWith('HOG-OS-LP')) {
            return parseUnits("0.0006852", 18);
        } else if (depositTokenName.startsWith('GHOG-OS-LP')) {
            return parseUnits("0.0010278", 18);
        } else {
            return BigNumber.from(0);
        }
        /*  const rewardPerSecond = await poolContract.sharePerSecond();
         if (depositTokenName.startsWith('HOG')) {
             console.log("ggggg", rewardPerSecond.mul(42000).div(60000).toString());
             return rewardPerSecond.mul(42000).div(60000);
         } else {
             return rewardPerSecond.mul(18000).div(60000);
         }*/
    }

    async getElysiumTokenPerSecond(bank: any) {
        const poolContract = this.contracts["CemeteryHogOsRewardPool"];
        const depositTokenName = bank.depositTokenName;
        try {
            // Call sharePerSecond as a property instead of a function
            const rewardPerSecond = await poolContract.sharePerSecond();
            if (depositTokenName.startsWith('HOG')) {
                return rewardPerSecond.mul(6000).div(60000);
            } else {
                return rewardPerSecond.mul(54000).div(60000);
            }
        } catch (error) {
            console.error('Error getting sharePerSecond:', error);
            return 0; // Return 0 or another default value in case of error
        }
    }
    /**
     * Method to calculate the tokenPrice of the deposited asset in a pool/bank
     * If the deposited token is an LP it will find the price of its pieces
     * @param tokenName
     * @param pool
     * @param token
     * @returns
     */
    async getDepositTokenPriceInDollars(tokenName: string, token: ERC20) {
        let tokenPrice;
        const priceOfOneFtmInDollars = await this.getOSPriceFromDexScreener();
        const dexScreenerTokens = ['OS', 'ANON', 'MCLB', 'SWPx', 'stS', 'scUSD', 'INDI', 'Beets', 'GOGLZ'];

        if (tokenName === 'S' || tokenName === "wS") {
            tokenPrice = priceOfOneFtmInDollars;
        } else if (tokenName === 'OS') {
            // Special handling for OS token
            tokenPrice = await this.getOSPairPrice();
        } else if (dexScreenerTokens.includes(tokenName)) {
            // Check cache first
            const cachedPrice = this.priceCache.get(token.address);
            const now = Date.now();

            if (cachedPrice && (now - cachedPrice.timestamp) < this.CACHE_DURATION) {
                tokenPrice = cachedPrice.price;
            } else {
                try {
                    const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${token.address}`);
                    const data = await response.json();

                    if (data.pairs && data.pairs.length > 0) {
                        const sortedPairs = data.pairs.sort((a: any, b: any) =>
                            Number(b.liquidity?.usd || 0) - Number(a.liquidity?.usd || 0)
                        );

                        const bestPair = sortedPairs[0];
                        tokenPrice = bestPair.priceUsd || '0';

                        // Cache the new price
                        this.priceCache.set(token.address, {
                            price: tokenPrice,
                            timestamp: now
                        });
                    } else {
                        console.warn(`No pairs found for token ${tokenName}`);
                        tokenPrice = '0';
                    }
                } catch (error) {
                    console.error(`Error fetching price for ${tokenName}:`, error);
                    tokenPrice = '0';
                }
            }
        } else if (tokenName.endsWith('-LP')) {
            const isTomb = tokenName.startsWith('HOG');
            tokenPrice = await this.getLPTokenPrice(token, this.externalTokens[tokenName.substr(0, tokenName.indexOf('-'))], isTomb);
        } else {
            tokenPrice = await this.getTokenPriceFromCustomApi(token);
            tokenPrice = (Number(tokenPrice)).toString();
        }

        return tokenPrice;
    }

    //===================================================================
    //===================== GET ASSET STATS =============================
    //=========================== END ===================================
    //===================================================================

    async getCurrentEpoch(): Promise<BigNumber> {
        const { TreasuryV3 } = this.contracts;
        return TreasuryV3.epoch();
    }

    async getBondOraclePriceInLastTWAP(): Promise<BigNumber> {
        const { TreasuryV3 } = this.contracts;
        return TreasuryV3.getBondPremiumRate();
    }

    /**
     * Buy bonds with cash.
     * @param amount amount of cash to purchase bonds with.
     */
    async buyBonds(amount: string | number): Promise<TransactionResponse> {
        const { TreasuryV3 } = this.contracts;
        const treasuryTombPrice = await TreasuryV3.getHogPrice();
        return await TreasuryV3.buyBonds(decimalToBalance(amount), treasuryTombPrice);
    }

    /**
     * Redeem bonds for cash.
     * @param amount amount of bonds to redeem.
     */
    async redeemBonds(amount: string): Promise<TransactionResponse> {
        const { TreasuryV3 } = this.contracts;
        const priceForTomb = await TreasuryV3.getHogPrice();
        return await TreasuryV3.redeemBonds(decimalToBalance(amount), priceForTomb);
    }

    async getTotalValueLocked(): Promise<Number> {
        let totalValue = 0;
        // Calculate TVL from all pools
        for (const bankInfo of Object.values(bankDefinitions)) {
            const pool = this.contracts[bankInfo.contract];
            const token = bankInfo.depositTokenName === "HOG" ? this.HOG :
                bankInfo.depositTokenName === "GHOG" ? this.GHOG :
                    this.externalTokens[bankInfo.depositTokenName];

            let tokenAmountInPool;
            if (bankInfo.contract === 'CemeteryHogOsRewardPool') {
                // Create contract instance for 0x4331Cb6903D9c6C0723721A77435a610E1e933Cf
                const hogOsContract = new Contract('0x4331Cb6903D9c6C0723721A77435a610E1e933Cf', GaugeABI, this.provider);
                // Check balance of 0xa7141905e2972c295577882552bede5406daf5ec in this contract
                tokenAmountInPool = await hogOsContract.balanceOf('0xa7141905e2972c295577882552bede5406daf5ec');
            } else if (bankInfo.contract === 'CemeteryGHogOsRewardPool') {
                // Create contract instance for 0x5342E272769e797e4bF34698437Eb28bF49c315d
                const ghogOsContract = new Contract('0x5342E272769e797e4bF34698437Eb28bF49c315d', GaugeABI, this.provider);
                // Check balance of 0xa7141905e2972c295577882552bede5406daf5ec in this contract
                // For GHOG-OS pool, check balance at 0x5342E272769e797e4bF34698437Eb28bF49c315d
                tokenAmountInPool = await ghogOsContract.balanceOf('0xa7141905e2972c295577882552bede5406daf5ec');
            } else {
                // For other pools, use the default behavior
                tokenAmountInPool = await token.balanceOf(pool.address);
            }
            console.log(tokenAmountInPool)
            const tokenPrice = await this.getDepositTokenPriceInDollars(bankInfo.depositTokenName, token);
            const value = Number(getDisplayBalance(tokenAmountInPool, token.decimal)) * Number(tokenPrice);
  
            const poolValue = Number.isNaN(value) ? 0 : value;
            totalValue += poolValue;
        }


        // Add GHOG staked in masonry to TVL
        const masonryGHOGBalance = await this.getStakedSharesOnMasonry();
        const masonryGHOGBalancev2 = await this.getStakedSharesOnMasonryV2();

        const ghogPrice = await this.getDepositTokenPriceInDollars("GHOG", this.GHOG);
        const masonryTVL = Number(getDisplayBalance(masonryGHOGBalance, this.GHOG.decimal)) * Number(ghogPrice);
        const masonryTVLv2 = Number(getDisplayBalance(masonryGHOGBalancev2, this.GHOG.decimal)) * Number(ghogPrice);
        totalValue += Number.isNaN(masonryTVL) ? 0 : masonryTVL;
        totalValue += Number.isNaN(masonryTVLv2) ? 0 : masonryTVLv2;

        return totalValue;
    }

    /**
     * Calculates the price of an LP token
     * Reference https://github.com/DefiDebauchery/discordpricebot/blob/4da3cdb57016df108ad2d0bb0c91cd8dd5f9d834/pricebot/pricebot.py#L150
     * @param lpToken the token under calculation
     * @param token the token pair used as reference (the other one would be S in most cases)
     * @param isTomb sanity check for usage of HOG token or GHOG
     * @returns price of the LP token
     */
    async getLPTokenPrice(lpToken: ERC20, token: ERC20, isTomb: boolean): Promise<string> {
        const totalSupply = getDisplayBalance(await lpToken.totalSupply(), lpToken.decimal, lpToken.decimal);
        //Get amount of tokenA
        const tokenSupply = getDisplayBalance(await token.balanceOf(lpToken.address), token.decimal, token.decimal);
        // const stat = isTomb === true ? await this.getTombStat() : await this.getShareStat();
        const stat = token.symbol === "HOG" || token.symbol === "GHOG" ? (isTomb === true ? await this.getTombStat() : await this.getShareStat()) : await this.getExternalTokenPrice(token.symbol);

        if (lpToken.symbol === "HOG-OS-LP") { // HOG-S-LP is a stable pair
            const sonicPrice = await this.getOSPairPrice();
            const sonicLpBalance = getDisplayBalance(await this.externalTokens["OS"].balanceOf(lpToken.address), this.externalTokens["OS"].decimal, this.externalTokens["OS"].decimal);
            const priceOfToken = stat.priceInDollars;
            const ratioOfSonicInLp = Number(sonicLpBalance) / (Number(sonicLpBalance) + Number(tokenSupply));
            const lpPriceInDollarsRatioAdjusted = (Number(sonicPrice) * ratioOfSonicInLp) + (Number(priceOfToken) * (1 - ratioOfSonicInLp));
            const tokenInLP = (Number(tokenSupply) + Number(sonicLpBalance)) / Number(totalSupply);
            const tokenPrice = (Number(lpPriceInDollarsRatioAdjusted) * tokenInLP) //We multiply by 2 since half the price of the lp token is the price of each piece of the pair. So twice gives the total
                .toString();

            return tokenPrice;

        } else if (lpToken.symbol === "GHOG-OS-LP") { // GHOG-S-LP is a stable pair
            const sonicPrice = await this.getOSPairPrice();
            const sonicLpBalance = getDisplayBalance(await this.externalTokens["OS"].balanceOf(lpToken.address), this.externalTokens["OS"].decimal, this.externalTokens["OS"].decimal);
            const priceOfToken = stat.priceInDollars;
            const ratioOfSonicInLp = Number(sonicLpBalance) / (Number(sonicLpBalance) + Number(tokenSupply));
            const lpPriceInDollarsRatioAdjusted = (Number(sonicPrice) * ratioOfSonicInLp) + (Number(priceOfToken) * (1 - ratioOfSonicInLp));
            const tokenInLP = (Number(tokenSupply) + Number(sonicLpBalance)) / Number(totalSupply);
            const tokenPrice = (Number(lpPriceInDollarsRatioAdjusted) * tokenInLP) //We multiply by 2 since half the price of the lp token is the price of each piece of the pair. So twice gives the total
                .toString();

            return tokenPrice;
        } else {
            const priceOfToken = stat.priceInDollars;
            const tokenInLP = Number(tokenSupply) / Number(totalSupply);
            const tokenPrice = (Number(priceOfToken) * tokenInLP * 2) //We multiply by 2 since half the price of the lp token is the price of each piece of the pair. So twice gives the total
                .toString();
            return tokenPrice;
        }
    }

    async earnedFromBank(
        poolName: ContractName,
        earnTokenName: String,
        poolId: Number,
        account = this.myAccount,
    ): Promise<BigNumber> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (account)
            userAccount = account;

        const pool = this.contracts[poolName];
        try {
            if (earnTokenName === 'HOG') {
                return await pool.pendingHOG(poolId, userAccount);
            } else {
                return await pool.pendingShare(poolId, userAccount);
            }
        } catch (err: any) {
            console.error(`Failed to call earned() on pool ${pool.address}: ${err.stack}`);
            return BigNumber.from(0);
        }
    }

    async stakedBalanceOnBank(poolName: ContractName, poolId: Number, account = this.myAccount): Promise<BigNumber> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (account)
            userAccount = account;

        console.log("ccccc", poolName)
        let stakeInPool = BigNumber.from(0)
        const pool = this.contracts[poolName];
        if (poolName === 'CemeteryHogOsRewardPool') {
            const vaultContract = this.contracts["HogVaultAutocompounder"];
            stakeInPool = await vaultContract.balanceOf(this.myAccount)
        } else if (poolName === 'CemeteryGHogOsRewardPool') {
            const vaultContract = this.contracts["GHogVaultAutocompounder"];
            stakeInPool = await vaultContract.balanceOf(this.myAccount)
        }
        try {
            let userInfo = await pool.userInfo(poolId, userAccount);
            return await userInfo.amount
        } catch (err: any) {
            console.error(`Failed to call balanceOf() on pool ${pool.address}: ${err.stack}`);
            return BigNumber.from(0);
        }
    }

    async stakedBalanceOnBankSummed(poolName: ContractName, poolId: Number, account = this.myAccount): Promise<BigNumber> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (account)
            userAccount = account;

        console.log("ccccc", poolName)
        let stakeInPool = BigNumber.from(0)
        const pool = this.contracts[poolName];
        if (poolName === 'CemeteryHogOsRewardPool') {
            const vaultContract = this.contracts["HogVaultAutocompounder"];
            stakeInPool = await vaultContract.balanceOf(this.myAccount)
        } else if (poolName === 'CemeteryGHogOsRewardPool') {
            const vaultContract = this.contracts["GHogVaultAutocompounder"];
            stakeInPool = await vaultContract.balanceOf(this.myAccount)
        }
        try {
            let userInfo = await pool.userInfo(poolId, userAccount);
            return await userInfo.amount.add(stakeInPool);
        } catch (err: any) {
            console.error(`Failed to call balanceOf() on pool ${pool.address}: ${err.stack}`);
            return BigNumber.from(0);
        }
    }

    /**
     * Deposits token to given pool.
     * @param poolName A name of pool contract.
     * @param amount Number of tokens with decimals applied. (e.g. 1.45 DAI * 10^18)
     * @returns {string} Transaction hash
     */

    async stake(poolName: ContractName, poolId: Number, sectionInUI: Number, amount: BigNumber): Promise<TransactionResponse> {
        const pool = this.contracts[poolName];
        return sectionInUI !== 3
            ? await pool.deposit(poolId, amount)
            : await pool.create(poolId, amount);
    }

    /**
     * Withdraws token from given pool.
     * @param poolName A name of pool contract.
     * @param amount Number of tokens with decimals applied. (e.g. 1.45 DAI * 10^18)
     * @returns {string} Transaction hash
     */
    async unstake(poolName: ContractName, poolId: Number, amount: BigNumber): Promise<TransactionResponse> {
        const pool = this.contracts[poolName];
        return await pool.withdraw(poolId, amount);
    }

    /**
     * Transfers earned token reward from given pool to my account.
     */

    async harvest(poolName: ContractName, poolId: Number, sectionInUI: Number): Promise<TransactionResponse> {
        const pool = this.contracts[poolName];
        //By passing 0 as the amount, we are asking the contract to only redeem the reward and not the currently staked token
        return sectionInUI !== 3
            ? await pool.withdraw(poolId, 0)
            : await pool.claim();
    }

    /**
     * Transfers earned token reward from given pool to my account.
     */

    async harvestPsm(poolName: ContractName, poolId: Number, payAmount: number): Promise<TransactionResponse> {
        const pool = this.contracts[poolName];
        const payAmountSlippage = 1.01
        const payAmountAdjusted = payAmount * payAmountSlippage
        //By passing 0 as the amount, we are asking the contract to only redeem the reward and not the currently staked token
        return await pool.harvest(poolId, { value: parseUnits(payAmountAdjusted.toFixed(18), 18) })
    }

    async harvestAllPsm(payAmount: number): Promise<TransactionResponse> {
        const pool = this.contracts["CemeteryGhogRewardPool"];
        const payAmountSlippage = 1.01
        const payAmountAdjusted = payAmount * payAmountSlippage
        //By passing 0 as the amount, we are asking the contract to only redeem the reward and not the currently staked token
        return await pool.harvestAll({ value: parseUnits(payAmountAdjusted.toFixed(18), 18) })
    }

    /**
     * Harvests and withdraws deposited tokens from the pool.
     */
    async exit(poolName: ContractName, poolId: Number, account = this.myAccount): Promise<TransactionResponse> {
        const pool = this.contracts[poolName];
        let userInfo = await pool.userInfo(poolId, account);
        return await pool.withdraw(poolId, userInfo.amount);
    }

    async fetchMasonryVersionOfUser(): Promise<string> {
        return 'latest';
    }

    currentMasonry(): Contract {
        if (!this.masonryVersionOfUser) {
            //throw new Error('you must unlock the wallet to continue.');
        }
        return this.contracts.Masonry;
    }

    currentMasonryV2(): Contract {
        if (!this.masonryVersionOfUser) {
            //throw new Error('you must unlock the wallet to continue.');
        }
        return this.contracts.MasonryV3;
    }

    isOldMasonryMember(): boolean {
        return this.masonryVersionOfUser !== 'latest';
    }

    private async fetchTokenPricesFromShadowApi(): Promise<void> {
        const SHADOW_API_URL = "http://localhost:5000/token-prices";
        const now = Date.now();

        if (this.tokenPriceCache.data && now - this.tokenPriceCache.timestamp < this.CACHE_TTL) {
            return; // Use cached data if it's still fresh
        }

        // Prevent multiple concurrent fetches
        if (this.fetching) {
            return;
        }
        this.fetching = true;

        try {
            const response = await fetch(SHADOW_API_URL);
            if (!response.ok) console.log(`HTTP error! Status: ${response.status}`);

            const rawData = await response.json();
            if (!rawData || typeof rawData !== "object") throw new Error("Invalid response format");

            const data = JSON.parse(JSON.stringify(rawData));

            this.tokenPriceCache.data = data;
            this.tokenPriceCache.timestamp = now;

            // Find the existing HOG token and update its price
            const hogToken = data["HOG"];
            const osPriceUSD = Number(this.tokenPriceCache.data["OS"]?.priceUSD || "0");
            const hogPriceInSonic = Number(this.tokenPriceCache.data["HOG"]?.priceUSD || "0");

            if (hogToken) {
                hogToken.priceUSD = (Number(formatUnits(hogPriceInSonic, 18)) * osPriceUSD).toString();
            } else {
                // If for some reason it wasn't found, push a new one (fallback)
                data.push({
                    decimals: 18,
                    id: "0xB3804bF38bD170ef65b4De8536d19a8e3600C0A9",
                    name: "HOG",
                    price: 0,
                    priceUSD: (Number(formatUnits(hogPriceInSonic, 18)) * osPriceUSD).toString(),
                    symbol: "HOG"
                });
            }

            // Find the existing HOG token and update its price
            const ghogToken = data["GHOG"];
            const ghogPriceInSonic = await this.GHOGOSLP.quote(this.GHOG.address, parseUnits("1"), 0.01);


            if (ghogToken) {
                ghogPriceInSonic.priceUSD = (Number(formatUnits(ghogPriceInSonic, 18)) * osPriceUSD).toString();
            } else {
                // If for some reason it wasn't found, push a new one (fallback)
                data.push({
                    decimals: 18,
                    id: "0x0e899dA2aD0817ed850ce68f7f489688E4D42D9D",
                    name: "GHOG",
                    price: 0,
                    priceUSD: (Number(formatUnits(ghogPriceInSonic, 18)) * osPriceUSD).toString(),
                    symbol: "GHOG"
                });
            }

            this.tokenPriceCache.data = data;
            this.tokenPriceCache.timestamp = now;

        } catch (err) {
            console.error(`Failed to fetch token prices: ${err}`);
        } finally {
            this.fetching = false; // Reset flag after fetching completes
        }
    }

    async getTokenPriceFromCustomApi(tokenContract: ERC20): Promise<string> {
        const dexScreenerTokens = ['HOG', 'GHOG', 'OS', 'ANON', 'MCLB', 'SWPx', 'stS', 'scUSD', 'INDI', 'Beets', 'GOGLZ'];

        if (tokenContract.symbol === "S") {
            tokenContract = this.externalTokens["wS"];
        }

        if (dexScreenerTokens.includes(tokenContract.symbol)) {
            // Check cache first
            const cachedPrice = this.priceCache.get(tokenContract.address);
            const now = Date.now();

            if (cachedPrice && (now - cachedPrice.timestamp) < this.CACHE_DURATION) {
                return cachedPrice.price;
            }

            try {
                const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${tokenContract.address}`);
                const data = await response.json();

                if (data.pairs && data.pairs.length > 0) {
                    let bestPair;
                    
                    // Special case for GHOG - use specific pair
                    if (tokenContract.symbol === 'GHOG') {
                        bestPair = data.pairs.find(pair => pair.pairAddress.toLowerCase() === '0xD1CB1622a50506F0fDdf329CB857a0935C7FbbF9'.toLowerCase());
                        if (!bestPair) {
                            console.warn('Specified GHOG pair not found, falling back to default sorting');
                        }
                    }

                    // If no specific pair found or not GHOG, use default sorting
                    if (!bestPair) {
                        const sortedPairs = data.pairs.sort((a: any, b: any) =>
                            Number(b.liquidity?.usd || 0) - Number(a.liquidity?.usd || 0)
                        );
                        bestPair = sortedPairs[0];
                    }

                    console.log("bestPair", bestPair);
                    const price = bestPair.priceUsd || '0';

                    // Cache the new price
                    this.priceCache.set(tokenContract.address, {
                        price,
                        timestamp: now
                    });

                    return price;
                }
                console.warn(`No pairs found for token ${tokenContract.symbol}`);
                return '0';
            } catch (error) {
                console.error(`Error fetching price for ${tokenContract.symbol}:`, error);
                return '0';
            }
        }

        // Fallback to original logic for other tokens
        await this.fetchTokenPricesFromShadowApi();
        return this.findTokenPrice(tokenContract.symbol);
    }

    async getTokenPriceFromCustomApiHog(tokenContract: ERC20): Promise<string> {
        const dexScreenerTokens = ['HOG'];
        if (dexScreenerTokens.includes(tokenContract.symbol)) {
            try {
                const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${tokenContract.address}`);
                const data = await response.json();

                if (data.pairs && data.pairs.length > 0) {
                    let selectedPair;

                    // Find HOG pair with address 0x784dd93f3c42dcbf88d45e6ad6d3cc20da169a60
                    selectedPair = data.pairs.find(
                        (pair: any) => pair.pairAddress.toLowerCase() === '0x784dd93f3c42dcbf88d45e6ad6d3cc20da169a60'.toLowerCase()
                    );

                    if (selectedPair) {
                        const price = selectedPair.priceNative || '0';
                        console.log("osprice3", price)
                        return price;
                    }

                    console.warn(`No suitable pair found for token ${tokenContract.symbol}`);
                    return '0';
                }
                console.warn(`No pairs found for token ${tokenContract.symbol}`);
                return '0';
            } catch (error) {
                console.error(`Error fetching price for ${tokenContract.symbol}:`, error);
                return '0';
            }
        }

        // Fallback to original logic for other tokens
        return this.findTokenPrice(tokenContract.symbol);
    }

    private findTokenPrice(symbol: string): string {
        // if (symbol === "ANON") { symbol = "Anon"; }
        const token = this.tokenPriceCache.data?.[symbol];

        return token ? token.priceUSD.toString() : "0";
    }

    async getMultipleTokenPricesFromCustomApi(tokenSymbols: string[]): Promise<{ [symbol: string]: string }> {
        // await this.fetchTokenPricesFromShadowApi(); // Ensure fresh prices if needed

        const prices: { [symbol: string]: string } = {};

        tokenSymbols.forEach((symbol) => {
            prices[symbol] = this.findTokenPrice(symbol);
        });

        return prices;
    }


    /*async getTokenPriceFromSpookySwap(tokenContract: ERC20): Promise<string> {
        const ready = await this.provider.ready;
        if (!ready) return;
        const chainId = 146;
        // const { chainId } = this.config;

        const { S } = this.externalTokens;

        const wS = new TokenSpooky(chainId, S.address, S.decimal);
        const token = new TokenSpooky(chainId, tokenContract.address, tokenContract.decimal, tokenContract.symbol);
        try {
            const wSToToken = await FetcherSpooky.fetchPairData(wS, token, this.provider);
            const liquidityToken = wSToToken.liquidityToken;
            let ftmBalanceInLP = await S.balanceOf(liquidityToken.address);
            let ftmAmount = Number(getDisplayBalance(ftmBalanceInLP, S.decimal, 8));
            let shibaBalanceInLP = await tokenContract.balanceOf(liquidityToken.address);
            let shibaAmount = Number(getDisplayBalance(shibaBalanceInLP, tokenContract.decimal, tokenContract.decimal));
            const priceOfOneFtmInDollars = await this.getOSPriceFromDexScreener();
            let priceOfShiba = (ftmAmount / shibaAmount) * Number(priceOfOneFtmInDollars);
            return priceOfShiba.toString();
        } catch (err) {
            console.error(`Failed to fetch token price of ${tokenContract.symbol}: ${err}`);
        }
    }

    async getTokenPriceFromPancakeswap(tokenContract: ERC20): Promise<string> {
        const ready = await this.provider.ready;
        if (!ready) return;
        const chainId = 146;
        // const { chainId } = this.config;
        const { S } = this.config.externalTokens;

        const wS = new TokenSpooky(chainId, S[0], S[1]);
        const token = new TokenSpooky(chainId, tokenContract.address, tokenContract.decimal, tokenContract.symbol);
        try {
            const wSToToken = await FetcherSpooky.fetchPairData(wS, token, this.provider);
            const priceInBUSD = new RouteSpooky([wSToToken], token);

            return priceInBUSD.midPrice.toFixed(4);
        } catch (err) {
            console.error(`Failed to fetch token price of ${tokenContract.symbol}: ${err}`);
            return '0';
        }
    }

    async getTokenPriceFromSpiritswap(tokenContract: ERC20): Promise<string> {
        const ready = await this.provider.ready;
        if (!ready) return;
        const chainId = 146;
        // const { chainId } = this.config;

        const { S } = this.externalTokens;

        const wS = new TokenSpooky(chainId, S.address, S.decimal);
        const token = new TokenSpooky(chainId, tokenContract.address, tokenContract.decimal, tokenContract.symbol);
        try {
            const wSToToken = await FetcherSpooky.fetchPairData(wS, token, this.provider);
            const liquidityToken = wSToToken.liquidityToken;
            let ftmBalanceInLP = await S.balanceOf(liquidityToken.address);
            let ftmAmount = Number(getDisplayBalance(ftmBalanceInLP, S.decimal, 8));
            let shibaBalanceInLP = await tokenContract.balanceOf(liquidityToken.address);
            let shibaAmount = Number(getDisplayBalance(shibaBalanceInLP, tokenContract.decimal, tokenContract.decimal));
            const priceOfOneFtmInDollars = await this.getOSPriceFromDexScreener();
            let priceOfShiba = (ftmAmount / shibaAmount) * Number(priceOfOneFtmInDollars);
            return priceOfShiba.toString();
        } catch (err) {
            console.error(`Failed to fetch token price of ${tokenContract.symbol}: ${err}`);
        }
    }*/

    async getOSPriceFromDexScreener(): Promise<string> {
        const sonicPrice = await this.getOSPairPrice();
        console.log("sonicPrice", sonicPrice);
        return sonicPrice;
    }

    async getHOGPriceFromDexScreener(): Promise<string> {
        const sonicPrice = await this.getTokenPriceFromCustomApiHog(this.externalTokens["HOG"]);
        return sonicPrice;
    }

    // async getOSPriceFromDexScreener(): Promise<string> {
    //     const ready = await this.provider.ready;
    //     if (!ready) return;
    //     const { S, USDC } = this.externalTokens;
    //     try {
    //         const fusdt_wS_lp_pair = this.externalTokens['S-USDC-LP'];
    //         let ftm_amount_BN = await S.balanceOf(fusdt_wS_lp_pair.address);
    //         let ftm_amount = Number(getFullDisplayBalance(ftm_amount_BN, S.decimal));
    //         let fusdt_amount_BN = await USDC.balanceOf(fusdt_wS_lp_pair.address);
    //         let fusdt_amount = Number(getFullDisplayBalance(fusdt_amount_BN, USDC.decimal));
    //         return ((fusdt_amount / Number(1)) / ftm_amount).toString();
    //     } catch (err) {
    //         console.error(`Failed to fetch token price of wS: ${err}`);
    //     }
    // }

    //===================================================================
    //===================================================================
    //===================== MASONRY METHODS =============================
    //===================================================================
    //===================================================================

    async getMasonryAPR() {
        try {
            console.log('carlossss: Starting getMasonryAPR calculation...');

            const stakedAmount = await this.getTotalStakedInMasonryV2();
            console.log('carlossss: Staked Amount:', stakedAmount.toString());

            const GHOGPrice = (await this.getShareStat()).priceInDollars;
            console.log('carlossss: GHOG Price:', GHOGPrice);

            // Convert stakedAmount from BigNumber to normal number with proper decimals
            const stakedAmountNormal = Number(getDisplayBalance(stakedAmount));
            const stakedAmountUsd = stakedAmountNormal * Number(GHOGPrice);
            console.log('carlossss: Staked Amount in USD:', stakedAmountUsd);

            const emissionRate = await this.getMasonryExpansionRate();
            // Convert emission rate from BigNumber
            const emissionRateNormal = Number(emissionRate.toString()) / 1e5; // Assuming 4 decimals
            console.log('carlossss: Emission Rate:', emissionRateNormal);

            const HOGStats = await this.getTombStat();
            const HOGCirculatingSupply = HOGStats.circulatingSupply;
            const HOGPrice = HOGStats.priceInDollars;
            console.log('carlossss: HOG Circulating Supply:', HOGCirculatingSupply);
            console.log('carlossss: HOG Price:', HOGPrice);

            const marketcap = Number(HOGCirculatingSupply) * Number(HOGPrice);
            console.log('carlossss: Market Cap:', marketcap);

            const efficient = (emissionRateNormal) * marketcap;
            console.log('carlossss: Efficient:', efficient);

            const final = efficient / stakedAmountUsd * 100;
            console.log('carlossss: Final (daily):', final);

            const final2 = (final) * 4 * 365;
            console.log('carlossss: Final APR:', final2);

            return final2;
        } catch (error) {
            console.error('carlossss: Error in getMasonryAPR:', error);
            throw error;
        }
    }

    async getMasonryExpansionRate() {
        const { TreasuryV3 } = this.contracts;
        const expansionRate: BigNumber = await TreasuryV3.maxSupplyExpansionPercent();
        console.log('Expansion Rate:', expansionRate);
        return expansionRate;
    }

    /**
     * Checks if the user is allowed to retrieve their reward from the Masonry
     * @returns true if user can withdraw reward, false if they can't
     */
    async hasAgreedToVaultTerms(contract: Contract): Promise<boolean> {
        return await contract.hasReadAndAcceptedTerms(this.myAccount);
    }

    /**
     * Checks if the user is allowed to retrieve their reward from the Masonry
     * @returns true if user can withdraw reward, false if they can't
     */
    async canUserClaimRewardFromMasonry(): Promise<boolean> {
        const Masonry = this.currentMasonry();
        return await Masonry.canClaimReward(this.myAccount);
    }

    async canUserClaimRewardFromMasonryV2(): Promise<boolean> {
        const MasonryV3 = this.currentMasonryV2();
        return await MasonryV3.canClaimReward(this.myAccount);
    }

    /**
     * Checks if the user is allowed to retrieve their reward from the Masonry
     * @returns true if user can withdraw reward, false if they can't
     */
    async rewardsWillBeBurnedFromMasonry(): Promise<boolean> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const Masonry = this.currentMasonry();
        const canWithdraw = await Masonry.rewardsWillBeBurned(userAccount);
        const stakedAmount = await this.getStakedSharesOnMasonry();
        const notStaked = Number(getDisplayBalance(stakedAmount, this.GHOG.decimal)) === 0;
        const result = notStaked ? false : canWithdraw;
        return result;
    }

    async rewardsWillBeBurnedFromMasonryV2(): Promise<boolean> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const MasonryV3 = this.currentMasonryV2();
        const canWithdraw = await MasonryV3.rewardsWillBeBurned(userAccount);
        const stakedAmount = await this.getStakedSharesOnMasonry();
        const notStaked = Number(getDisplayBalance(stakedAmount, this.GHOG.decimal)) === 0;
        const result = notStaked ? false : canWithdraw;
        return result;
    }

    async canUserUnstakeFromMasonry(): Promise<boolean> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const Masonry = this.currentMasonry();
        const canWithdraw = await Masonry.canWithdraw(userAccount);
        const stakedAmount = await this.getStakedSharesOnMasonry();
        const notStaked = Number(getDisplayBalance(stakedAmount, this.GHOG.decimal)) === 0;
        const result = notStaked ? true : canWithdraw;
        return result;
    }

    async canUserUnstakeFromMasonryV2(): Promise<boolean> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const MasonryV3 = this.currentMasonryV2();
        const canWithdraw = await MasonryV3.canWithdraw(userAccount);
        const stakedAmount = await this.getStakedSharesOnMasonry();
        const notStaked = Number(getDisplayBalance(stakedAmount, this.GHOG.decimal)) === 0;
        const result = notStaked ? true : canWithdraw;
        return result;
    }


    async timeUntilClaimRewardFromMasonry(): Promise<BigNumber> {
        // const Masonry = this.currentMasonry();
        // const mason = await Masonry.masons(this.myAccount);
        return BigNumber.from(0);
    }

    async getTotalStakedInMasonry(): Promise<BigNumber> {
        const Masonry = this.currentMasonry();
        return await Masonry.totalSupply();
    }

    async getTotalStakedInMasonryV2(): Promise<BigNumber> {
        const MasonryV3 = this.currentMasonryV2();
        return await MasonryV3.totalSupply();
    }

    async stakeShareToMasonry(amount: string): Promise<TransactionResponse> {
        if (this.isOldMasonryMember()) {
            throw new Error("you're using old nest. please withdraw and deposit the GHOG again.");
        }
        const Masonry = this.currentMasonry();
        return await Masonry.stake(decimalToBalance(amount));
    }

    async stakeShareToMasonryV2(amount: string): Promise<TransactionResponse> {
        if (this.isOldMasonryMember()) {
            throw new Error("you're using old nest. please withdraw and deposit the GHOG again.");
        }
        const MasonryV3 = this.currentMasonryV2();
        return await MasonryV3.stake(decimalToBalance(amount));
    }


    async getStakedSharesOnMasonry(): Promise<BigNumber> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const Masonry = this.currentMasonry();
        return await Masonry.balanceOf(userAccount);
    }

    async getStakedSharesOnMasonryV2(): Promise<BigNumber> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const MasonryV3 = this.currentMasonryV2();
        return await MasonryV3.balanceOf(userAccount);
    }

    async getEarningsOnMasonry(): Promise<BigNumber> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const Masonry = this.currentMasonry();
        return await Masonry.earned(userAccount);
    }

    async getEarningsOnMasonryV2(): Promise<BigNumber> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const MasonryV3 = this.currentMasonryV2();
        return await MasonryV3.earned(userAccount);
    }

    async withdrawShareFromMasonry(amount: string): Promise<TransactionResponse> {
        const Masonry = this.currentMasonry();
        return await Masonry.withdraw(decimalToBalance(amount));
    }

    async withdrawShareFromMasonryV2(amount: string): Promise<TransactionResponse> {
        const MasonryV3 = this.currentMasonryV2();
        return await MasonryV3.withdraw(decimalToBalance(amount));
    }

    async harvestCashFromMasonry(): Promise<TransactionResponse> {
        const Masonry = this.currentMasonry();
        if (this.masonryVersionOfUser === 'v1') {
            return await Masonry.claimDividends();
        }
        return await Masonry.claimReward();
    }

    async harvestCashFromMasonryV2(): Promise<TransactionResponse> {
        const MasonryV3 = this.currentMasonryV2();
        if (this.masonryVersionOfUser === 'v1') {
            return await MasonryV3.claimDividends();
        }
        return await MasonryV3.claimReward();
    }

    async exitFromMasonry(): Promise<TransactionResponse> {
        const Masonry = this.currentMasonry();
        return await Masonry.exit();
    }

    async exitFromMasonryV2(): Promise<TransactionResponse> {
        const MasonryV3 = this.currentMasonryV2();
        return await MasonryV3.exit();
    }

    async getTreasuryNextAllocationTime(): Promise<AllocationTime> {
        const { TreasuryV3 } = this.contracts;
        const nextEpochTimestamp: BigNumber = await TreasuryV3.nextEpochPoint();
        const nextAllocation = new Date(nextEpochTimestamp.mul(1000).toNumber());
        const prevAllocation = new Date(Date.now());

        return { from: prevAllocation, to: nextAllocation };
    }
    /**
     * This method calculates and returns in a from to to format
     * the period the user needs to wait before being allowed to claim
     * their reward from the masonry
     * @returns Promise<AllocationTime>
     */
    async getUserClaimRewardTime(): Promise<AllocationTime> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const { Masonry, Treasury } = this.contracts;
        const nextEpochTimestamp = await Masonry.nextEpochPoint(); //in unix timestamp
        const currentEpoch = await Masonry.epoch();
        const mason = await Masonry.masons(userAccount);
        const startTimeEpoch = mason.epochTimerStart;
        const period = await Treasury.PERIOD();
        const periodInHours = period / 60 / 60; // 6 hours, period is displayed in seconds which is 21600
        const rewardLockupEpochs = await Masonry.rewardLockupEpochs();
        const targetEpochForClaimUnlock = Number(startTimeEpoch) + Number(rewardLockupEpochs);

        const fromDate = new Date(Date.now());
        if (targetEpochForClaimUnlock - currentEpoch <= 0) {
            return { from: fromDate, to: fromDate };
        } else if (targetEpochForClaimUnlock - currentEpoch === 1) {
            const toDate = new Date(nextEpochTimestamp * 1000);
            return { from: fromDate, to: toDate };
        } else {
            const toDate = new Date(nextEpochTimestamp * 1000);
            const delta = targetEpochForClaimUnlock - currentEpoch - 1;
            const endDate = moment(toDate)
                .add(delta * periodInHours, 'hours')
                .toDate();
            return { from: fromDate, to: endDate };
        }
    }

    async getUserClaimRewardTimeV2(): Promise<AllocationTime> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const { MasonryV3, TreasuryV3 } = this.contracts;
        const nextEpochTimestamp = await MasonryV3.nextEpochPoint(); //in unix timestamp
        const currentEpoch = await MasonryV3.epoch();
        const mason = await MasonryV3.masons(userAccount);
        const startTimeEpoch = mason.epochTimerStart;
        const period = await TreasuryV3.PERIOD();
        const periodInHours = period / 60 / 60; // 6 hours, period is displayed in seconds which is 21600
        const rewardLockupEpochs = await MasonryV3.rewardLockupEpochs();
        const targetEpochForClaimUnlock = Number(startTimeEpoch) + Number(rewardLockupEpochs);

        const fromDate = new Date(Date.now());
        if (targetEpochForClaimUnlock - currentEpoch <= 0) {
            return { from: fromDate, to: fromDate };
        } else if (targetEpochForClaimUnlock - currentEpoch === 1) {
            const toDate = new Date(nextEpochTimestamp * 1000);
            return { from: fromDate, to: toDate };
        } else {
            const toDate = new Date(nextEpochTimestamp * 1000);
            const delta = targetEpochForClaimUnlock - currentEpoch - 1;
            const endDate = moment(toDate)
                .add(delta * periodInHours, 'hours')
                .toDate();
            return { from: fromDate, to: endDate };
        }
    }

    /**
     * This method calculates and returns in a from to to format
     * the period the user needs to wait before being allowed to unstake
     * from the masonry
     * @returns Promise<AllocationTime>
     */
    async getUserBurnRewardsTime(): Promise<AllocationTime> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const { Masonry, Treasury } = this.contracts;
        const nextEpochTimestamp = await Masonry.nextEpochPoint();
        const currentEpoch = await Masonry.epoch();
        const mason = await Masonry.masons(userAccount);
        const startTimeEpoch = mason.epochTimerStart;
        const period = await Treasury.PERIOD();
        const PeriodInHours = period / 60 / 60;
        const withdrawLockupEpochs = await Masonry.claimRewardsBurnEpochs();
        const fromDate = new Date(Date.now());
        const targetEpochForClaimUnlock = Number(startTimeEpoch) + Number(withdrawLockupEpochs);
        const stakedAmount = await this.getStakedSharesOnMasonry();
        if (currentEpoch <= targetEpochForClaimUnlock && Number(stakedAmount) === 0) {
            return { from: fromDate, to: fromDate };
        } else if (targetEpochForClaimUnlock - currentEpoch === 1) {
            const toDate = new Date(nextEpochTimestamp * 1000);
            return { from: fromDate, to: toDate };
        } else {
            const toDate = new Date(nextEpochTimestamp * 1000);
            const delta = targetEpochForClaimUnlock - Number(currentEpoch) - 1;
            const endDate = moment(toDate)
                .add(delta * PeriodInHours, 'hours')
                .toDate();
            return { from: fromDate, to: endDate };
        }
    }

    async getUserBurnRewardsTimeV2(): Promise<AllocationTime> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const { MasonryV3, TreasuryV3 } = this.contracts;
        const nextEpochTimestamp = await MasonryV3.nextEpochPoint();
        const currentEpoch = await MasonryV3.epoch();
        const mason = await MasonryV3.masons(userAccount);
        const startTimeEpoch = mason.epochTimerStart;
        const period = await TreasuryV3.PERIOD();
        const PeriodInHours = period / 60 / 60;
        const withdrawLockupEpochs = await MasonryV3.claimRewardsBurnEpochs();
        const fromDate = new Date(Date.now());
        const targetEpochForClaimUnlock = Number(startTimeEpoch) + Number(withdrawLockupEpochs);
        const stakedAmount = await this.getStakedSharesOnMasonry();
        if (currentEpoch <= targetEpochForClaimUnlock && Number(stakedAmount) === 0) {
            return { from: fromDate, to: fromDate };
        } else if (targetEpochForClaimUnlock - currentEpoch === 1) {
            const toDate = new Date(nextEpochTimestamp * 1000);
            return { from: fromDate, to: toDate };
        } else {
            const toDate = new Date(nextEpochTimestamp * 1000);
            const delta = targetEpochForClaimUnlock - Number(currentEpoch) - 1;
            const endDate = moment(toDate)
                .add(delta * PeriodInHours, 'hours')
                .toDate();
            return { from: fromDate, to: endDate };
        }
    }

    async getUserUnstakeTime(): Promise<AllocationTime> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const { Masonry, Treasury } = this.contracts;
        const nextEpochTimestamp = await Masonry.nextEpochPoint();
        const currentEpoch = await Masonry.epoch();
        const mason = await Masonry.masons(userAccount);
        const startTimeEpoch = mason.epochTimerStart;
        const period = await Treasury.PERIOD();
        const PeriodInHours = period / 60 / 60;
        const withdrawLockupEpochs = await Masonry.withdrawLockupEpochs();
        const fromDate = new Date(Date.now());
        const targetEpochForClaimUnlock = Number(startTimeEpoch) + Number(withdrawLockupEpochs);
        const stakedAmount = await this.getStakedSharesOnMasonry();
        if (currentEpoch <= targetEpochForClaimUnlock && Number(stakedAmount) === 0) {
            return { from: fromDate, to: fromDate };
        } else if (targetEpochForClaimUnlock - currentEpoch === 1) {
            const toDate = new Date(nextEpochTimestamp * 1000);
            return { from: fromDate, to: toDate };
        } else {
            const toDate = new Date(nextEpochTimestamp * 1000);
            const delta = targetEpochForClaimUnlock - Number(currentEpoch) - 1;
            const endDate = moment(toDate)
                .add(delta * PeriodInHours, 'hours')
                .toDate();
            return { from: fromDate, to: endDate };
        }
    }

    async getUserUnstakeTimeV2(): Promise<AllocationTime> {
        let userAccount = "0x187b7579DE421E30BfAd0b46625a7455AB552975"
        if (this.myAccount)
            userAccount = this.myAccount;

        const { MasonryV3, TreasuryV3 } = this.contracts;
        const nextEpochTimestamp = await MasonryV3.nextEpochPoint();
        const currentEpoch = await MasonryV3.epoch();
        const mason = await MasonryV3.masons(userAccount);
        const startTimeEpoch = mason.epochTimerStart;
        const period = await TreasuryV3.PERIOD();
        const PeriodInHours = period / 60 / 60;
        const withdrawLockupEpochs = await MasonryV3.withdrawLockupEpochs();
        const fromDate = new Date(Date.now());
        const targetEpochForClaimUnlock = Number(startTimeEpoch) + Number(withdrawLockupEpochs);
        const stakedAmount = await this.getStakedSharesOnMasonry();
        if (currentEpoch <= targetEpochForClaimUnlock && Number(stakedAmount) === 0) {
            return { from: fromDate, to: fromDate };
        } else if (targetEpochForClaimUnlock - currentEpoch === 1) {
            const toDate = new Date(nextEpochTimestamp * 1000);
            return { from: fromDate, to: toDate };
        } else {
            const toDate = new Date(nextEpochTimestamp * 1000);
            const delta = targetEpochForClaimUnlock - Number(currentEpoch) - 1;
            const endDate = moment(toDate)
                .add(delta * PeriodInHours, 'hours')
                .toDate();
            return { from: fromDate, to: endDate };
        }
    }

    async watchAssetInMetamask(assetName: string): Promise<boolean> {
        const { ethereum } = window as any;
        if (ethereum && ethereum.networkVersion === config.chainId.toString()) {
            let asset;
            let assetUrl;
            if (assetName === 'HOG') {
                asset = this.HOG;
                assetUrl = 'https://i.ibb.co/5GTmcxn/hog-logo.png';
            } else if (assetName === 'GHOG') {
                asset = this.GHOG;
                assetUrl = 'https://i.ibb.co/nkSChnR/golden-hog-logo.png';
            } else if (assetName === 'BHOG') {
                asset = this.BHOG;
                assetUrl = 'https://i.ibb.co/gtwsPzN/bhog-logo.png';
            }
            await ethereum.request({
                method: 'wallet_watchAsset',
                params: {
                    type: 'ERC20',
                    options: {
                        address: asset.address,
                        symbol: asset.symbol,
                        decimals: 18,
                        image: assetUrl,
                    },
                },
            });
        }
        return true;
    }

    async provideTombFtmLP(ftmAmount: string, tombAmount: BigNumber): Promise<TransactionResponse> {
        const { TaxOffice } = this.contracts;

        return await TaxOffice.addLiquidityUnderPeg(this.externalTokens["USDC.e"].address, tombAmount, parseUnits(ftmAmount, 6), tombAmount, parseUnits(ftmAmount, 6));
    }

    async quoteFromSwapXRouter(tokenAmount: string, tokenName: string): Promise<string> {
        try {
            // Convert human readable amount to wei
            const amountIn = parseUnits(tokenAmount, 18);

            // Get token addresses based on tokenName
            const tokenIn = this.HOG.address

            // Call quote with proper parameters
            // Using granularity of 1 for a simple quote
            const quote = await this.HOGOSLP.quote(
                tokenIn,
                amountIn,
                1  // granularity parameter
            );

            // Convert result back to human readable format
            return formatUnits(quote, 18);
        } catch (error) {
            console.error("Error in quoteFromSwapXRouter:", error);
            throw error;
        }
    }

    /**
     * @returns an array of the regulation events till the most up to date epoch
     */
    async listenForRegulationsEvents(): Promise<any> {
        const { Treasury } = this.contracts;

        const treasuryDaoFundedFilter = Treasury.filters.DaoFundFunded();
        const treasuryTeamFundedFilter = Treasury.filters.TeamFundFunded();
        const treasuryDevFundedFilter = Treasury.filters.DevFundFunded();
        const treasuryMasonryFundedFilter = Treasury.filters.MasonryFunded();
        const boughtBondsFilter = Treasury.filters.BoughtBonds();
        const redeemBondsFilter = Treasury.filters.RedeemedBonds();

        let epochBlocksRanges: any[] = [];
        let masonryFundEvents = await Treasury.queryFilter(treasuryMasonryFundedFilter);
        var events: any[] = [];
        masonryFundEvents.forEach(function callback(value, index) {
            events.push({ epoch: index + 1 });
            events[index].masonryFund = getDisplayBalance(value.args[1]);
            if (index === 0) {
                epochBlocksRanges.push({
                    index: index,
                    startBlock: value.blockNumber,
                    boughBonds: 0,
                    redeemedBonds: 0,
                });
            }
            if (index > 0) {
                epochBlocksRanges.push({
                    index: index,
                    startBlock: value.blockNumber,
                    boughBonds: 0,
                    redeemedBonds: 0,
                });
                epochBlocksRanges[index - 1].endBlock = value.blockNumber;
            }
        });

        epochBlocksRanges.forEach(async (value, index) => {
            events[index].bondsBought = await this.getBondsWithFilterForPeriod(
                boughtBondsFilter,
                value.startBlock,
                value.endBlock,
            );
            events[index].bondsRedeemed = await this.getBondsWithFilterForPeriod(
                redeemBondsFilter,
                value.startBlock,
                value.endBlock,
            );
        });
        let TEAMFundEvents = await Treasury.queryFilter(treasuryTeamFundedFilter);
        TEAMFundEvents.forEach(function callback(value, index) {
            events[index].teamFund = getDisplayBalance(value.args[1]);
        });
        let DEVFundEvents = await Treasury.queryFilter(treasuryDevFundedFilter);
        DEVFundEvents.forEach(function callback(value, index) {
            events[index].devFund = getDisplayBalance(value.args[1]);
        });
        let DAOFundEvents = await Treasury.queryFilter(treasuryDaoFundedFilter);
        DAOFundEvents.forEach(function callback(value, index) {
            events[index].daoFund = getDisplayBalance(value.args[1]);
        });
        return events;
    }

    async listenForRegulationsEventsV2(): Promise<any> {
        const { TreasuryV3 } = this.contracts;

        const treasuryDaoFundedFilter = TreasuryV3.filters.DaoFundFunded();
        const treasuryTeamFundedFilter = TreasuryV3.filters.TeamFundFunded();
        const treasuryDevFundedFilter = TreasuryV3.filters.DevFundFunded();
        const treasuryMasonryFundedFilter = TreasuryV3.filters.MasonryFunded();
        const boughtBondsFilter = TreasuryV3.filters.BoughtBonds();
        const redeemBondsFilter = TreasuryV3.filters.RedeemedBonds();

        let epochBlocksRanges: any[] = [];
        let masonryFundEvents = await TreasuryV3.queryFilter(treasuryMasonryFundedFilter);
        var events: any[] = [];
        masonryFundEvents.forEach(function callback(value, index) {
            events.push({ epoch: index + 1 });
            events[index].masonryFund = getDisplayBalance(value.args[1]);
            if (index === 0) {
                epochBlocksRanges.push({
                    index: index,
                    startBlock: value.blockNumber,
                    boughBonds: 0,
                    redeemedBonds: 0,
                });
            }
            if (index > 0) {
                epochBlocksRanges.push({
                    index: index,
                    startBlock: value.blockNumber,
                    boughBonds: 0,
                    redeemedBonds: 0,
                });
                epochBlocksRanges[index - 1].endBlock = value.blockNumber;
            }
        });

        epochBlocksRanges.forEach(async (value, index) => {
            events[index].bondsBought = await this.getBondsWithFilterForPeriod(
                boughtBondsFilter,
                value.startBlock,
                value.endBlock,
            );
            events[index].bondsRedeemed = await this.getBondsWithFilterForPeriod(
                redeemBondsFilter,
                value.startBlock,
                value.endBlock,
            );
        });
        let TEAMFundEvents = await TreasuryV3.queryFilter(treasuryTeamFundedFilter);
        TEAMFundEvents.forEach(function callback(value, index) {
            events[index].teamFund = getDisplayBalance(value.args[1]);
        });
        let DEVFundEvents = await TreasuryV3.queryFilter(treasuryDevFundedFilter);
        DEVFundEvents.forEach(function callback(value, index) {
            events[index].devFund = getDisplayBalance(value.args[1]);
        });
        let DAOFundEvents = await TreasuryV3.queryFilter(treasuryDaoFundedFilter);
        DAOFundEvents.forEach(function callback(value, index) {
            events[index].daoFund = getDisplayBalance(value.args[1]);
        });
        return events;
    }

    /**
     * Helper method
     * @param filter applied on the query to the treasury events
     * @param from block number
     * @param to block number
     * @returns the amount of bonds events emitted based on the filter provided during a specific period
     */
    async getBondsWithFilterForPeriod(filter: EventFilter, from: number, to: number): Promise<number> {
        const { TreasuryV3 } = this.contracts;
        const bondsAmount = await TreasuryV3.queryFilter(filter, from, to);
        return bondsAmount.length;
    }

    async estimateZapIn(tokenName: string, lpName: string, amount: string): Promise<number[]> {
        try {
            const { ZapHogOs, ZapGHogOS } = this.contracts;

            const zapper = lpName === 'HOG-OS-LP'
                ? ZapHogOs
                : ZapGHogOS;

            if (!zapper) {
                console.error('Zapper contract not found for', lpName);
                return [0, 0];
            }

            // Get the correct token address
            const token = tokenName === OS_TICKER
                ? this.OS
                : tokenName === HOG_TICKER
                    ? this.HOG
                    : this.GHOG;

            if (!token) {
                console.error('Token not found for', tokenName);
                return [0, 0];
            }

            const estimate = await zapper.estimateZapIn(
                token.address,
                parseUnits(amount, 18)
            );

            // Ensure we return a valid number even if estimate is undefined
            const estimateValue = estimate ? Number(estimate) / 1e18 : 0;
            return [estimateValue, 0]; // Return array with two values for token0 and token1
        } catch (error) {
            console.error('Error in estimateZapIn:', error);
            return [0, 0]; // Return safe default values in case of error
        }
    }

    async zapIn(tokenName: string, lpName: string, amount: string): Promise<TransactionResponse> {
        try {
            const { ZapHogOs, ZapGHogOS } = this.contracts;

            const zapper = lpName === 'HOG-OS-LP'
                ? ZapHogOs
                : ZapGHogOS;

            if (!zapper) {
                throw new Error(`Zapper contract not found for ${lpName}`);
            }

            // Get the correct token address
            const token = tokenName === OS_TICKER
                ? this.OS
                : tokenName === HOG_TICKER
                    ? this.HOG
                    : this.GHOG;

            if (!token) {
                throw new Error(`Token not found for ${tokenName}`);
            }

            if (!this.myAccount) {
                throw new Error('Wallet not connected');
            }

            // For HOG, GHOG, or OS tokens
            return await zapper.zapInToken(
                token.address,
                parseUnits(amount, 18),
                this.myAccount // Add the user's wallet address
            );

        } catch (error) {
            console.error('Error in zapIn:', error);
            throw error; // Re-throw the error since this is a transaction
        }
    }

    
    async swapTBondToTShare(tbondAmount: BigNumber): Promise<TransactionResponse> {
        const { TShareSwapper } = this.contracts;
        return await TShareSwapper.swapTBondToTShare(tbondAmount);
    }
    async estimateAmountOfTShare(tbondAmount: string): Promise<string> {
        const { TShareSwapper } = this.contracts;
        try {
            const estimateBN = await TShareSwapper.estimateAmountOfTShare(parseUnits(tbondAmount, 18));
            return getDisplayBalance(estimateBN, 18, 6);
        } catch (err) {
            console.error(`Failed to fetch estimate tshare amount: ${err}`);
        }
    }

    async getTShareSwapperStat(address: string): Promise<TShareSwapperStat> {
        const { TShareSwapper } = this.contracts;
        const tshareBalanceBN = await TShareSwapper.getTShareBalance();
        const tbondBalanceBN = await TShareSwapper.getTBondBalance(address);
        // const tombPriceBN = await TShareSwapper.getTombPrice();
        // const tsharePriceBN = await TShareSwapper.getTSharePrice();
        const rateTSharePerTombBN = await TShareSwapper.getTShareAmountPerTomb();
        const tshareBalance = getDisplayBalance(tshareBalanceBN, 18, 5);
        const tbondBalance = getDisplayBalance(tbondBalanceBN, 18, 5);
        return {
            tshareBalance: tshareBalance.toString(),
            tbondBalance: tbondBalance.toString(),
            // tombPrice: tombPriceBN.toString(),
            // tsharePrice: tsharePriceBN.toString(),
            rateTSharePerTomb: rateTSharePerTombBN.toString(),
        };
    }

    /* Nest new functions */

    async allocateSeigniorage(): Promise<TransactionResponse> {
        const treasury = this.contracts["TreasuryV3"];
        return await treasury.allocateSeigniorage();
    }

    /* PResale functions */

    async enterPresale(amount: string): Promise<TransactionResponse> {
        if (!this.myAccount) return;
        const { Presale } = this.contracts;

        return await Presale.enterPresale(this.myAccount, { value: ethers.utils.parseEther(amount) });
    }

    async getPresaleStats(): Promise<any> {
        const { Presale } = this.contracts;

        const presaleTotal = 75000;
        const hogAvailable = await this.HOG.balanceOf(Presale.address);
        const hogSold = presaleTotal - (Number(formatUnits(hogAvailable)));

        return {
            hogSold: hogSold
        }
    }

    async getOSPairPrice(): Promise<string> {
        const TOKEN_ADDRESS = '0xb1e25689D55734FD3ffFc939c4C3Eb52DFf8A794';
        const PAIR_ADDRESS = '0xa76Beaf111BaD5dD866fa4835D66b9aA2Eb1FdEc';

        // Check cache first
        const cachedPrice = this.priceCache.get(PAIR_ADDRESS);
        const now = Date.now();

        if (cachedPrice && (now - cachedPrice.timestamp) < this.CACHE_DURATION) {
            return cachedPrice.price;
        }

        try {
            const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${TOKEN_ADDRESS}`);
            const data = await response.json();

            if (data.pairs && data.pairs.length > 0) {
                const targetPair = data.pairs.find(
                    (pair: any) => pair.pairAddress.toLowerCase() === PAIR_ADDRESS.toLowerCase()
                );

                if (targetPair) {
                    const price = targetPair.priceUsd || '0';

                    // Cache the new price
                    this.priceCache.set(PAIR_ADDRESS, {
                        price,
                        timestamp: now
                    });

                    return price;
                }
            }
            console.warn('No pair data found for OS pair');
            return '0';
        } catch (error) {
            console.error('Error fetching OS pair price:', error);
            return '0';
        }
    }


    async stakedBalanceOnAutoCompounder(poolName: ContractName, account = this.myAccount): Promise<BigNumber> {
        const pool = this.contracts[poolName];
        try {
            let userInfo = await pool.balanceOf(account);
            return await userInfo;
        } catch (err) {
            console.error(`Failed to call balanceOf() on pool ${pool.address}: ${err.stack}`);
            return BigNumber.from(0);
        }
    }

    async getAutocompounderAPRs(bank: AutoCompounder): Promise<AutocompounderStats> {
        if (this.myAccount === undefined) return;
        const depositToken = bank.depositToken; // LP PAIR CONTRACT

        const poolContract = this.contracts[bank.masterChefContract];
        const vaultContract = this.contracts[bank.vaultContract];
        const autocompounderContract = this.contracts[bank.autocompounderContract];

        const farmMasterchef = await autocompounderContract.masterChef();

        let farmTVL;
        if (bank.contract === 'HogAutocompounder') {
            // Create contract instance for 0x4331Cb6903D9c6C0723721A77435a610E1e933Cf
            const hogOsContract = new Contract('0x4331Cb6903D9c6C0723721A77435a610E1e933Cf', GaugeABI, this.provider);
            // Check balance of 0xa7141905e2972c295577882552bede5406daf5ec in this contract
            farmTVL = await hogOsContract.balanceOf('0xa7141905e2972c295577882552bede5406daf5ec');
        } else if (bank.contract === 'GHogAutocompounder') {
            // Create contract instance for 0x5342E272769e797e4bF34698437Eb28bF49c315d
            const ghogOsContract = new Contract('0x5342E272769e797e4bF34698437Eb28bF49c315d', GaugeABI, this.provider);
            // Check balance of 0xa7141905e2972c295577882552bede5406daf5ec in this contract
            // For GHOG-OS pool, check balance at 0x5342E272769e797e4bF34698437Eb28bF49c315d
            farmTVL = await ghogOsContract.balanceOf('0xa7141905e2972c295577882552bede5406daf5ec');
        }
        
        else if (bank.contract === 'CemeteryGHogOsRewardPool') {
            // Create contract instance for 0x5342E272769e797e4bF34698437Eb28bF49c315d
            const ghogOsContract = new Contract('0x5342E272769e797e4bF34698437Eb28bF49c315d', GaugeABI, this.provider);
            // Check balance of 0xa7141905e2972c295577882552bede5406daf5ec in this contract
            // For GHOG-OS pool, check balance at 0x5342E272769e797e4bF34698437Eb28bF49c315d
            farmTVL = await ghogOsContract.balanceOf('0xa7141905e2972c295577882552bede5406daf5ec');
        } else {
            // For other pools, use the default behavior
            farmTVL = await depositToken.balanceOf(farmMasterchef);
        }

        const sarcophagiTVL = await autocompounderContract.balanceOfPool(); // DAR AQUI UM FIX?

        const depositTokenPrice = await this.getDepositTokenPriceInDollars(bank.depositTokenName, depositToken);
        // const depositTokenPrice = await (await this.getLPStat(bank.depositTokenName)).priceOfOne


        let TVL = Number(depositTokenPrice) * Number(getDisplayBalance(farmTVL, depositToken.decimal, depositToken.decimal));
        const sarcophagiTVLDisplay = (Number(getDisplayBalance(sarcophagiTVL, depositToken.decimal, depositToken.decimal))) * Number(depositTokenPrice);
        console.log("sarcophagiTVLDisplay", sarcophagiTVLDisplay)

        const yourLps = await vaultContract.balanceOf(this.myAccount)

        const cumulativeDeposits = await vaultContract.cumulativeDeposits(this.myAccount)
        const cumulativeWithdrawals = await vaultContract.cumulativeWithdrawals(this.myAccount)
        const myShares = await this.externalTokens[bank.depositTokenName === "HOG-OS-LP" ? "HOGOS_ac" : "GHOGOS_ac"].balanceOf(this.myAccount)


        const mySharesDisplay = Number(getDisplayBalance(myShares, depositToken.decimal, depositToken.decimal))

        const profitInTokens = Number(cumulativeWithdrawals ? (cumulativeWithdrawals / 10 ** 18) : 0) - Number(cumulativeDeposits ? (cumulativeDeposits / 10 ** 18) : 0)
        const profitInTokensDollars = (Number(cumulativeWithdrawals ? (cumulativeWithdrawals / 10 ** 18) : 0) - Number(cumulativeDeposits ? (cumulativeDeposits / 10 ** 18) : 0)) * Number(depositTokenPrice)

        // const stat = bank.earnTokenName === 'HOG' || bank.earnTokenName === 'GHOG' ? (bank.earnTokenName === 'HOG' ? await this.getTombStat() : await this.getShareStat()) : ( bank.earnTokenName === 'SinSPIRIT' || bank.earnTokenName === 'SPIRIT' ? await this.getExternalTokenPrice('SPIRIT') : await this.getExternalTokenPrice(bank.earnTokenName));
        const stat = bank.earnTokenName === 'HOG' || bank.earnTokenName === 'GHOG' ? (bank.earnTokenName === 'HOG' ? await this.getTombStat() : await this.getShareStat()) : (bank.earnTokenName === 'SinSPIRIT' ? await this.getExternalTokenPrice('SinSPIRIT') : (bank.earnTokenName === 'SPIRIT' ? (await this.getExternalTokenPrice('SPIRIT')) : (await this.getExternalTokenPrice(bank.earnTokenName))));
        const tokenPerSecond = BigNumber.from(await this.getElysiumTokenPerSecond(bank));

        console.log("tokenPerSecond", tokenPerSecond)
        const vaultBalance = await vaultContract.balance()
        const vaultTotalSupply = await this.externalTokens[bank.depositTokenName === "HOG-OS-LP" ? "HOGOS_ac" : "GHOGOS_ac"].totalSupply()
        const realLPsStaked = vaultBalance.toString() === '0' ? BigNumber.from('0') : await vaultBalance.mul(yourLps).div(vaultTotalSupply)

        const realLPsStakedDisplay = Number(getDisplayBalance(realLPsStaked, depositToken.decimal, depositToken.decimal))




        const tokenPerHour = tokenPerSecond.eq(0) ? BigNumber.from(0) : tokenPerSecond.mul(60).mul(60);
        const totalRewardPricePerYear = tokenPerHour.eq(0) ? 0 : Number(stat.priceInDollars) * Number(getDisplayBalance(tokenPerHour.mul(24).mul(365)));
        const totalRewardPricePerDay = tokenPerHour.eq(0) ? 0 : Number(stat.priceInDollars) * Number(getDisplayBalance(tokenPerHour.mul(24)));
        const totalRewardPricePerWeek = tokenPerHour.eq(0) ? 0 : Number(stat.priceInDollars) * Number(getDisplayBalance(tokenPerHour.mul(24).mul(7)));
        const totalRewardPricePerMonth = tokenPerHour.eq(0) ? 0 : Number(stat.priceInDollars) * Number(getDisplayBalance(tokenPerHour.mul(24).mul(30)));

        let totalStakingTokenInPool =
            Number(depositTokenPrice) * Number(getDisplayBalance(farmTVL, depositToken.decimal, depositToken.decimal));

        const yourLpsFormatted = Number(getDisplayBalance(yourLps, depositToken.decimal, depositToken.decimal)) !== 0 ? Number(getDisplayBalance(yourLps, depositToken.decimal, depositToken.decimal)) : 0;

        const dailyAPR = (totalRewardPricePerDay / totalStakingTokenInPool) * 100;
        const weeklyAPR = (totalRewardPricePerWeek / totalStakingTokenInPool) * 100;
        const monthlyAPR = (totalRewardPricePerMonth / totalStakingTokenInPool) * 100;
        const yearlyAPR = (totalRewardPricePerYear / totalStakingTokenInPool) * 100;

        // const contractFeeCall = await autocompounderContract.callFee(); // 1000
        // const contractFeeTreasury = await autocompounderContract.treasuryFee(); // 9000
        // const contractFeeSecurity = await autocompounderContract.securityFee(); // 10
        // const contractFeeTotal = await autocompounderContract.totalFee(); // 500
        // const percentDivisor = 10000;

        // const totalFees = contractFeeCall.add(contractFeeTreasury).add(contractFeeSecurity).add(contractFeeTotal).div(percentDivisor)
        const totalFees = 0.161 //%

        // uint256 withdrawFee = pairBal.mul(securityFee).div(PERCENT_DIVISOR);


        const feeDaily = (totalRewardPricePerDay / totalStakingTokenInPool) * totalFees * 100;
        const feeWeekly = (totalRewardPricePerWeek / totalStakingTokenInPool) * totalFees * 100;
        const feeMonthly = (totalRewardPricePerMonth / totalStakingTokenInPool) * totalFees * 100;
        const feeYearly = (totalRewardPricePerYear / totalStakingTokenInPool) * totalFees * 100;

        const rateDaily = dailyAPR - feeDaily
        const rateWeekly = weeklyAPR - feeWeekly
        const rateMonthly = monthlyAPR - feeMonthly
        const rateYearly = yearlyAPR - feeYearly
        return {
            yourLps: yourLpsFormatted.toFixed(8).toString(),
            dailyAPR: dailyAPR.toFixed(2).toString(),
            weeklyAPR: weeklyAPR.toFixed(2).toString(),
            monthlyAPR: monthlyAPR.toFixed(2).toString(),
            yearlyAPR: yearlyAPR.toFixed(2).toString(),
            feeDaily: feeDaily.toFixed(2).toString(),
            feeWeekly: feeWeekly.toFixed(2).toString(),
            feeMonthly: feeMonthly.toFixed(2).toString(),
            feeYearly: feeYearly.toFixed(2).toString(),
            rateDaily: rateDaily.toFixed(2).toString(),
            rateWeekly: rateWeekly.toFixed(2).toString(),
            rateMonthly: rateMonthly.toFixed(2).toString(),
            rateYearly: rateYearly.toFixed(2).toString(),
            TVL: TVL.toFixed(2).toString(),
            sarcophagiTVL: sarcophagiTVLDisplay.toFixed(4).toString(),
            profitInTokens: yourLpsFormatted > 0 ? (yourLpsFormatted + profitInTokens).toFixed(2).toString() : profitInTokens.toFixed(2).toString(),
            profitInTokensDollars: yourLpsFormatted > 0 ? (profitInTokensDollars + yourLpsFormatted * Number(depositTokenPrice)).toFixed(2).toString() : profitInTokensDollars.toFixed(2).toString(),
            totalLpsStaked: TVL.toFixed(2).toString(),
            myShares: mySharesDisplay.toFixed(8).toString(),
            realLPsStaked: realLPsStakedDisplay.toFixed(8).toString(),
        };
    }

    async stakedBalanceOnAutoCompounderDollars(poolName: ContractName, account = this.myAccount): Promise<number> {
        const pool = this.contracts[poolName];
        try {
            let userInfo = await pool.balanceOf(account);
            let sharePrice = await pool.getPricePerFullShare();
            // let sharePric2e = await pool.balance();
            // let sharePric23e = await pool.totalSupply();
            const displayUserInfo = getDisplayBalance(userInfo, 18)
            const displaySharePrice = getDisplayBalance(sharePrice, 18)
            const totalPrice = Number(displayUserInfo) * Number(displaySharePrice)
            // return await totalPrice;
            return totalPrice;
        } catch (err) {
            console.error(`Failed to call balanceOf() on pool ${pool.address}: ${err.stack}`);
            return 0;
        }
    }

    async autocompounderWithdrawAll(poolName: ContractName, account = this.myAccount): Promise<TransactionResponse> {
        const autocompounderVault = this.contracts[poolName];
        return await autocompounderVault.withdrawAll();
    }

    async stakeAutocompounder(poolName: ContractName, amount: BigNumber): Promise<TransactionResponse> {
        const autocompounder = this.contracts[poolName];

        return await autocompounder.deposit(amount);

    }

    async unstakeAutocompounder(poolName: ContractName, amount: BigNumber): Promise<TransactionResponse> {
        const pool = this.contracts[poolName];

        return await pool.withdraw(amount);

    }

    async getNextHarvestTime(bank: AutoCompounder): Promise<Date> {
        try {
            const linkCompounderContract = this.contracts[bank.compounderContract];
            const lastHarvestTime = await linkCompounderContract.lastHarvestTime();

            // Convert BigNumber timestamp to milliseconds and add 3 hours (3 * 60 * 60 * 1000 ms)
            const nextHarvestTime = new Date(lastHarvestTime.toNumber() * 1000 + (3 * 60 * 60 * 1000));

            return nextHarvestTime;
        } catch (error) {
            console.error('Error getting next harvest time:', error);
            return new Date(); // Return current time if there's an error
        }
    }

    async harvestAutoCompounder(bank: AutoCompounder): Promise<void> {
        try {
            const linkCompounderContract = this.contracts[bank.compounderContract];

            // Call the harvest function
            const tx = await linkCompounderContract.harvest();

            // Wait for the transaction to be mined
            await tx.wait();

            // You might want to show a success message or trigger a UI update
            console.log('Harvest successful');
        } catch (error) {
            console.error('Error harvesting:', error);
            throw error; // Re-throw the error so it can be handled by the UI
        }
    }

    async zapOut(lpName: string, amount: string): Promise<TransactionResponse> {
        try {
            const { ZapHogOs, ZapGHogOS } = this.contracts;

            const zapper = lpName === 'HOG-OS-LP'
                ? ZapHogOs
                : ZapGHogOS;

            if (!zapper) {
                throw new Error(`Zapper contract not found for ${lpName}`);
            }

            if (!this.myAccount) {
                throw new Error('Wallet not connected');
            }

            // Call the correct zap out function based on LP type
            if (lpName === 'HOG-OS-LP') {
                return await zapper.zapOutToHog(
                    parseUnits(amount, 18),
                    this.myAccount
                );
            } else {
                return await zapper.zapOutToGHog(
                    parseUnits(amount, 18),
                    this.myAccount
                );
            }

        } catch (error) {
            console.error('Error in zapOut:', error);
            throw error;
        }
    }

    // Add this new method to the TombFinance class
    async getAccountBalances(account: string = this.myAccount): Promise<AccountBalances> {
      try {
        if (!account) {
          return {
            hog: '0',
            ghog: '0',
            hogOsLp: '0',
            ghogOsLp: '0'
          };
        }

        // Get balances of all tokens
        const hogBalance = await this.HOG.balanceOf(account);
        const ghogBalance = await this.GHOG.balanceOf(account);
        const hogOsLpBalance = await this.HOG_OS_LP.balanceOf(account);
        const ghogOsLpBalance = await this.GHOG_OS_LP.balanceOf(account);

        // Add console.logs to debug the raw values
        console.log('Raw balances:', {
          hog: hogBalance.toString(),
          ghog: ghogBalance.toString(),
          hogOsLp: hogOsLpBalance.toString(),
          ghogOsLp: ghogOsLpBalance.toString()
        });

        // Convert balances to displayable format with 18 decimals (assuming these are standard ERC20 tokens)
        return {
          hog: getDisplayBalance(hogBalance, 18, 4),
          ghog: getDisplayBalance(ghogBalance, 18, 4),
          hogOsLp: getDisplayBalance(hogOsLpBalance, 18, 4),
          ghogOsLp: getDisplayBalance(ghogOsLpBalance, 18, 4)
        };
      } catch (error) {
        console.error('Error fetching account balances:', error);
        return {
          hog: '0',
          ghog: '0',
          hogOsLp: '0',
          ghogOsLp: '0'
        };
        }
    }

}
